import React from 'react';
import PropTypes from 'prop-types';
import lodashGroupBy from 'lodash/fp/groupBy';
import makeFullName from 'common/util/makeFullName';
import dateStringMomentWithCurrentTime from 'common/util/dateStringMomentWithCurrentTime';
import {
  UTILITY_ACTIVATION_ITEM_STATUS_SETUP_COMPLETED,
  UTILITY_ACTIVATION_ITEM_STATUS_COMPLETED,
  UTILITY_ACTIVATION_ITEM_STATUS_DROPOUT,
  UTILITY_ACTIVATION_UI_STATE_WAITING,
} from 'common/utility/constants';

import {
  makeAddressStreetDisplay,
  makeAddressCityStateString,
} from '@utilityprofit/address-helpers';

import SrCard from 'client/components/SrCard/SrCard';
import SrText from 'client/components/SrText';
import SrBox from 'client/components/SrBox';
import SrUtilityProfitActivationStatusSummary from 'client/components/SrUtilityProfitAdminProcessActivationPage/SrUtilityProfitActivationStatusSummary';
import SrUtilityProfitAdminProcessActivationForm from 'client/components/SrUtilityProfitAdminProcessActivationPage/SrUtilityProfitAdminProcessActivationForm';
import SrLink from 'client/components/SrLink/SrLink';
import SrUtilityProfitPartnerNotes from 'client/components/SrUtilityProfitPartnerNotes/SrUtilityProfitPartnerNotes';
import SrLinkUrlButton from 'client/components/SrLinkUrlButton';

import './SrUtilityProfitAdminProcessActivationPage.less';
import UPActivationFiles from 'client/components/UPActivationFiles/UPActivationFiles';
import { formatPhoneNumber } from 'common/formatters';

const SrUtilityProfitAdminProcessActivationPage = ({
  currentUser,
  utilityActivation,
  utilityProvidersList,
  utilityProvidersInRange = [],
  utilityProvidersByAddress = [],
  utilityTypes,
  utilityDealers,
  utilitySalesReps,
  updateProviderCoveragePoints,
  updateActivation,
  updateActivationItem,
  toggleRequiredType,
  onMapServiceAreaProvider,
  onConfirmMapping,
  backTitle,
  backUrl,
}) => {
  const { partner = {} } = utilityActivation;
  const utilityProvidersByTypeId = lodashGroupBy('utilityTypeId', utilityProvidersList);
  const utilityProvidersInRangeByTypeId = lodashGroupBy('utilityTypeId', utilityProvidersInRange);
  const utilityProvidersByAddressByTypeId = lodashGroupBy(
    'utilityTypeId',
    utilityProvidersByAddress,
  );
  const moveInDate = utilityActivation.leaseStartDate
    ? dateStringMomentWithCurrentTime(utilityActivation.leaseStartDate).format('MMM DD, YYYY')
    : 'NA';
  const tenantFields = utilityActivation.user
    ? [
        makeFullName(utilityActivation.user.firstName, utilityActivation.user.lastName),
        utilityActivation.user.phone,
        utilityActivation.user.email,
      ]
    : ['Pending signup'];

  const internetItemId = utilityActivation.items.find((item) => item.utilityTypeId === 1)?.id;

  return (
    <div className="SrUtilityProfitAdminProcessActivationPage">
      <div styleName="layoutColumns">
        <SrBox height="100vh" overflowY="auto" backgroundColor="#FFFFFF">
          <SrBox padding="34px 10px 15px" borderBottom="2px solid #e9ebec">
            {backTitle && backUrl && (
              <SrText
                font="greycliff"
                weight="800"
                size="16"
                display="block"
                paddingBottom="11px"
                lineHeight="24px"
              >
                <SrLink color="blue300" cursor="pointer" to={backUrl}>
                  {`< ${backTitle}`}
                </SrLink>
              </SrText>
            )}
            <SrText font="greycliff" weight="800" size="18" display="block" lineHeight="24px">
              {makeAddressStreetDisplay(utilityActivation.address)}
            </SrText>

            <SrText
              font="greycliff"
              weight="800"
              size="18"
              display="block"
              paddingBottom="11px"
              lineHeight="24px"
            >
              {makeAddressCityStateString(utilityActivation.address)}
            </SrText>
          </SrBox>
          <SrBox padding="15px 10px" borderBottom="2px solid #e9ebec">
            <SrBox marginBottom="10px" width="90%" paddingLeft="0">
              <SrLinkUrlButton
                buttonText="Copy Renter Utility Link"
                className="utilityActivationLink"
                hideUrl
                href={`https://utility-setup.com/${utilityActivation.partner.slug}/${utilityActivation.shortId}`}
              />
            </SrBox>
            <SrText font="greycliff" weight="800" size="14" display="block" lineHeight="20px">
              <SrLink
                color="blue300"
                cursor="pointer"
                target="_blank"
                to={`/utility-setup/${utilityActivation.partner.slug}/${utilityActivation.shortId}`}
              >
                Renter Activation Page ↗
              </SrLink>
              <ul className="unorderedList">
                <li>
                  <SrText
                    color="grey"
                    font="greycliff"
                    weight="600"
                    fontSize="14px"
                    lineHeight="25px"
                    padding="10px 10px 0 10px"
                  >
                    Move in date: {moveInDate}
                  </SrText>
                </li>
              </ul>
            </SrText>

            <SrText font="greycliff" weight="800" size="14" display="block" lineHeight="20px">
              <SrLink
                color="blue300"
                cursor="pointer"
                target="_blank"
                to={`/utility-profit/${utilityActivation.partner.slug}`}
              >
                Partner Overview Page ↗
              </SrLink>
            </SrText>
            <SrText
              color="grey"
              font="greycliff"
              weight="600"
              fontSize="14px"
              lineHeight="25px"
              padding="10px 10px 0 10px"
            >
              Partner: {partner.name}
            </SrText>
          </SrBox>
          <SrBox padding="10px 10px 15px" borderBottom="2px solid #e9ebec">
            <SrText font="greycliff" weight="800" size="14" display="block" lineHeight="20px">
              Tenant:
            </SrText>
            {tenantFields.map((str) => (
              <SrText
                key={str}
                font="greycliff"
                weight="600"
                size="14"
                display="block"
                lineHeight="20px"
              >
                {str}
              </SrText>
            ))}
          </SrBox>
          <SrBox padding="10px 10px 15px" borderBottom="2px solid #e9ebec">
            <SrText
              font="greycliff"
              weight="800"
              size="14"
              display="block"
              lineHeight="20px"
              paddingBottom="4px"
            >
              Partner Support for tenants:
            </SrText>
            <SrText font="greycliff" weight="600" size="14" display="block" lineHeight="20px">
              {formatPhoneNumber(partner.customerSupportPhone) || '--'}
            </SrText>
            <SrText font="greycliff" weight="600" size="14" display="block" lineHeight="20px">
              {partner.customerSupportEmail || '--'}
            </SrText>
          </SrBox>
          <SrBox padding="15px 10px" borderBottom="2px solid #e9ebec">
            <SrText font="greycliff" weight="800" size="18" display="block" paddingBottom="12px">
              Admin prep summary
            </SrText>
            <SrUtilityProfitActivationStatusSummary
              utilityTypes={utilityTypes}
              requiredTypes={utilityActivation.requiredTypes}
              items={utilityActivation.items}
              completedStates={[
                UTILITY_ACTIVATION_ITEM_STATUS_SETUP_COMPLETED,
                UTILITY_ACTIVATION_ITEM_STATUS_COMPLETED,
                UTILITY_ACTIVATION_ITEM_STATUS_DROPOUT,
              ]}
            />
          </SrBox>

          <SrBox padding="15px 10px" borderBottom="2px solid #e9ebec">
            <SrText font="greycliff" weight="800" size="18" display="block" paddingBottom="12px">
              Tenant setup summary
            </SrText>
            <SrUtilityProfitActivationStatusSummary
              utilityTypes={utilityTypes}
              requiredTypes={utilityActivation.requiredTypes}
              items={utilityActivation.items}
              defaultState={UTILITY_ACTIVATION_UI_STATE_WAITING}
            />
          </SrBox>
        </SrBox>
        <SrBox height="100vh" overflowY="auto" paddingTop="10px">
          <SrCard noContentPadding borderRadius="14px" maxWidth="100%">
            <SrBox padding="0 25px 25px">
              <SrText font="greycliff" size="21" weight="700">
                Fill in available utilities. Then upload tenant confirmations.
              </SrText>
            </SrBox>
            <SrUtilityProfitAdminProcessActivationForm
              currentUser={currentUser}
              utilityActivation={utilityActivation}
              utilityProvidersByTypeId={utilityProvidersByTypeId}
              utilityProvidersInRangeByTypeId={utilityProvidersInRangeByTypeId}
              utilityProvidersByAddressByTypeId={utilityProvidersByAddressByTypeId}
              utilityTypes={utilityTypes}
              utilityDealers={utilityDealers}
              utilitySalesReps={utilitySalesReps}
              updateProviderCoveragePoints={updateProviderCoveragePoints}
              updateActivation={updateActivation}
              updateActivationItem={updateActivationItem}
              toggleRequiredType={toggleRequiredType}
              onMapServiceAreaProvider={onMapServiceAreaProvider}
              onConfirmMapping={onConfirmMapping}
            />
          </SrCard>
        </SrBox>
        <SrBox height="100vh" overflowY="auto" paddingTop="10px" paddingRight="8px">
          <SrUtilityProfitPartnerNotes utilityPartnerId={utilityActivation.utilityPartnerId} />
          <UPActivationFiles
            title="Internet Activation Files"
            utilityActivationId={utilityActivation.id}
            utilityActivationItemId={internetItemId}
          />
        </SrBox>
      </div>
    </div>
  );
};

SrUtilityProfitAdminProcessActivationPage.propTypes = {
  utilityActivation: PropTypes.object.isRequired,
  utilityProvidersList: PropTypes.array.isRequired,
  utilityProvidersInRange: PropTypes.array,
  utilityTypes: PropTypes.array.isRequired,
  utilityDealers: PropTypes.array.isRequired,
  utilitySalesReps: PropTypes.array.isRequired,
  updateProviderCoveragePoints: PropTypes.func.isRequired,
  updateActivation: PropTypes.func.isRequired,
  updateActivationItem: PropTypes.func.isRequired,
  toggleRequiredType: PropTypes.func.isRequired,
  onMapServiceAreaProvider: PropTypes.func.isRequired,
  backUrl: PropTypes.string,
  backTitle: PropTypes.string,
};

SrUtilityProfitAdminProcessActivationPage.defaultProps = {
  backTitle: 'Back to Mapping Dashboard',
  backUrl: '/utility-profit/admin/dashboard/mapping-hitlist',
};

SrUtilityProfitAdminProcessActivationPage.displayName = 'SrUtilityProfitAdminProcessActivationPage';

export default SrUtilityProfitAdminProcessActivationPage;
